import { NgTemplateOutlet } from '@angular/common';
import { Component, DestroyRef, EventEmitter, HostListener, inject, Output, ViewChild } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormsModule } from '@angular/forms';
import {
  ACS_APP_NAME,
  AppComms,
  CARE_SCHEDULER_APP_NAME,
  CENTRAL_PAY_RECON_APP_NAME,
  HOME_APP_NAME,
  MATCH_APP_NAME,
  MED_BILL_APP_NAME,
  Module,
  MUTUAL_DRUG_ANALYTICS_APP_NAME,
  MY_DATA_MART_APP_NAME,
  OPE_APP_NAME,
  Organization,
  OrgStore,
  PROFIT_AMP_APP_NAME,
  RX_COMPASS_APP_NAME,
  RX_MENTOR_APP_NAME,
  SCHEDULE_APP_NAME,
  SOP_TOOL_APP_NAME,
  VAXREACH_APP_NAME
} from '@enliven/angular';
import { NgbDropdown, NgbDropdownModule, NgbTooltipModule } from '@ng-bootstrap/ng-bootstrap';
import { NgSelectComponent, NgSelectModule } from '@ng-select/ng-select';
import { TranslateModule } from '@ngx-translate/core';
import { Subject } from 'rxjs';
import { debounceTime, distinctUntilChanged, switchMap, tap } from 'rxjs/operators';
import { DataService } from 'src/app/services/data.service';
import { E360SessionService } from 'src/app/services/e360-session.service';
import { E360UserService } from 'src/app/services/e360-user.service';
import { HeaderService } from 'src/app/services/header.service';
import { StateService } from 'src/app/services/state.service';

@Component({
  selector: 'e360-header',
  standalone: true,
  imports: [NgSelectModule, FormsModule, TranslateModule, NgbDropdownModule, NgTemplateOutlet, NgbTooltipModule],
  templateUrl: './header.component.html',
  styleUrls: ['./header.component.scss']
})
export class HeaderComponent {
  @Output() appClick: EventEmitter<Module> = new EventEmitter();
  @ViewChild('appsDropdown') appsDropdown: NgbDropdown;
  @ViewChild('orgSelector') orgSelector: NgSelectComponent;
  @ViewChild('orgStoreSelector') orgStoreSelector: NgSelectComponent;

  private readonly ORG_PAGE_SIZE = 100;
  private readonly destroyRef = inject(DestroyRef);
  private currentOrgSearch = '';

  orgsLoading = false;
  orgsHasMore = true;
  orgsPage = 1;
  readonly orgSearch$ = new Subject<string>();

  HOME_APP_NAME: string = HOME_APP_NAME;

  // TODO: IP-11421 story will be used to remove these and use the new categories for the checks needed
  ACS_APP_NAME = ACS_APP_NAME;
  CARE_SCHEDULER_APP_NAME = CARE_SCHEDULER_APP_NAME;
  CENTRAL_PAY_RECON_APP_NAME = CENTRAL_PAY_RECON_APP_NAME;
  MATCH_APP_NAME = MATCH_APP_NAME;
  MED_BILL_APP_NAME = MED_BILL_APP_NAME;
  MUTUAL_DRUG_ANALYTICS_APP_NAME = MUTUAL_DRUG_ANALYTICS_APP_NAME;
  MY_DATA_MART_APP_NAME = MY_DATA_MART_APP_NAME;
  OPE_APP_NAME = OPE_APP_NAME;
  PROFIT_AMP_APP_NAME = PROFIT_AMP_APP_NAME;
  RX_COMPASS_APP_NAME = RX_COMPASS_APP_NAME;
  RX_MENTOR_APP_NAME = RX_MENTOR_APP_NAME;
  SCHEDULE_APP_NAME = SCHEDULE_APP_NAME;
  SOP_TOOL_APP_NAME = SOP_TOOL_APP_NAME;
  VAXREACH_APP_NAME = VAXREACH_APP_NAME;

  headerService: HeaderService = inject(HeaderService);
  dataService: DataService = inject(DataService);
  stateService: StateService = inject(StateService);
  e360UserService: E360UserService = inject(E360UserService);
  e360SessionService: E360SessionService = inject(E360SessionService);

  constructor() {
    this.orgSearch$
      .pipe(
        debounceTime(300),
        distinctUntilChanged(),
        tap(() => {
          this.orgsLoading = true;
          this.orgsPage = 1;
        }),
        switchMap(term => {
          this.currentOrgSearch = term ?? '';
          return this.dataService.getOrgsPage(1, this.ORG_PAGE_SIZE, this.currentOrgSearch);
        }),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe({
        next: ({ orgs, hasMore }) => {
          const selected = this.stateService.selectedOrg;
          const isEmptySearch = !this.currentOrgSearch.trim();
          const isSelectedInList = !selected || orgs.some(o => o.organizationId === selected.organizationId);
          this.stateService.orgs = isEmptySearch && !isSelectedInList ? [selected, ...orgs] : orgs;
          this.orgsHasMore = hasMore;
          this.orgsLoading = false;
        },
        error: () => {
          this.orgsLoading = false;
        }
      });
  }

  @HostListener('window:blur', ['$event'])
  closeDropdowns() {
    this.appsDropdown.close();
    this.orgSelector?.close();
    this.orgStoreSelector?.close();
  }

  @HostListener('window:message', ['$event'])
  onMessage({ data }: MessageEvent) {
    if (data.for === AppComms.SHELL_APP_NAME) {
      switch (data.operation) {
        case AppComms.ORG_ADDED: {
          this.orgsPage = 1;
          this.orgsHasMore = true;
          this.orgsLoading = true;
          this.dataService.getOrgsPage(1, this.ORG_PAGE_SIZE, this.currentOrgSearch).subscribe(({ orgs, hasMore }) => {
            const selected = this.stateService.selectedOrg;
            const isEmptySearch = !this.currentOrgSearch.trim();
            const isSelectedInList = !selected || orgs.some(o => o.organizationId === selected.organizationId);
            this.stateService.orgs = isEmptySearch && !isSelectedInList ? [selected, ...orgs] : orgs;
            this.orgsHasMore = hasMore;
            this.orgsLoading = false;
          });
          break;
        }
        case AppComms.ORG_STORE_ADDED: {
          this.dataService
            .getAllStoresForOrg(this.stateService.selectedOrg?.organizationId)
            .subscribe((stores: OrgStore[]) => {
              this.stateService.orgStores = stores;
              this.headerService.handleAllStoresEntry();
            });
          break;
        }
      }
    }
  }

  orgChanged(org: Organization) {
    this.e360SessionService.orgId = org.organizationId; // keep this for phase 1, will need to be removed in phase 2 IP-12015
    this.e360SessionService.setDefaultOrg({
      organizationId: org.organizationId,
      organizationIdType: 'organizationId'
    });
    this.e360SessionService.setDefaultStore(undefined);
    this.dataService.getAllStoresForOrg(org.organizationId).subscribe((stores: OrgStore[]) => {
      this.stateService.orgStores = stores;
      this.headerService.handleAllStoresEntry();
      this.stateService.sendStateToPluginApps();
    });
  }

  orgStoreChanged() {
    this.stateService.sendStateToPluginApps();
  }

  onOrgScrollToEnd() {
    if (this.orgsLoading || !this.orgsHasMore) return;

    this.orgsLoading = true;
    this.orgsPage++;

    this.dataService
      .getOrgsPage(this.orgsPage, this.ORG_PAGE_SIZE, this.currentOrgSearch)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: ({ orgs, hasMore }) => {
          this.stateService.orgs = [...(this.stateService.orgs || []), ...orgs];
          this.orgsHasMore = hasMore;
          this.orgsLoading = false;
        },
        error: () => {
          this.orgsPage--;
          this.orgsLoading = false;
        }
      });
  }
}
